import React, { Suspense } from 'react'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import ReactDOM from 'react-dom'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout, usePageWidth } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { useUser } from '@fs/zion-user'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'

const App = React.lazy(() => import('./App'))

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      integrations: [Sentry.extraErrorDataIntegration({ depth: 4 })],
      // Unhandled `TypeError: Failed to fetch` from third-party beacons (GTM, Google Ads,
      // Dynatrace), not our code. TREE-BUILDER-4PH/4PT. Remove if Zion adds it upstream.
      // `TypeError: Load failed` is Safari's wording of the same error. TREE-BUILDER-1ZM.
      // `Java object is gone` comes from scripts the Facebook/Instagram Android in-app
      // browser injects (iabjs://), not our code. TREE-BUILDER-481.
      // `Object Not Found Matching Id:N, MethodName:..., ParamCount:N` comes from the
      // CefSharp-based link scanner Microsoft Outlook/Defender Safe Links uses, not our code. TREE-BUILDER-4Q3.
      ignoreErrors: ['Failed to fetch', 'Load failed', 'Java object is gone', 'Object Not Found Matching Id'],
      debug: process.env.NODE_ENV === 'development',
    })
  )
}

function useHideHeaderFooter() {
  const hasHidehfParam = window?.location?.search.includes('hidehf')
  const hasHidehfSessionStorage = window?.sessionStorage?.getItem('hidehf') === 'true'
  const hidehf = hasHidehfParam || hasHidehfSessionStorage

  if (hidehf) {
    window?.sessionStorage?.setItem('hidehf', 'true')
  }

  const { signedIn, userLoading } = useUser()
  return {
    hideHeader: userLoading || hidehf,
    hideFooter: signedIn || userLoading || hidehf,
  }
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)
  const { hideHeader, hideFooter } = useHideHeaderFooter()
  const atWidth = usePageWidth()
  const helpChatbot = atWidth({ default: false, xl: true })

  return (
    <Router basename={base}>
      <Root analytics header={{ hidden: hideHeader }} footer={{ hidden: hideFooter }} helpChatbot={helpChatbot}>
        {/* The Root component will render the header and footer based on the props passed */}
        <AppLayout fullWidth>
          <App />
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
